import React, { useCallback, useEffect, useState } from "react";
import { partnerApi } from "../services/api";

const STATUS_OPTIONS = ["assigned", "confirmed", "completed", "cancelled"];

const STATUS_LABELS = {
  requested: "Requested",
  assigned: "Assigned",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function PartnerTestDrives() {
  const [bookings, setBookings] = useState([]);
  const [partnerName, setPartnerName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await partnerApi.testDrives();
      setBookings(res.data || []);
      setPartnerName(res.partner?.name || "");
    } catch (err) {
      setError(err.message || "Failed to load test drives");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (booking, nextStatus) => {
    setSavingId(booking.id);
    try {
      await partnerApi.updateTestDrive(booking.id, { status: nextStatus });
      await load();
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = statusFilter
    ? bookings.filter((b) => b.status === statusFilter)
    : bookings;

  return (
    <div>
      <div className="admin-toolbar">
        <div>
          <h2>Test Drive Inbox</h2>
          <p className="admin-muted" style={{ margin: "0.35rem 0 0" }}>
            Bookings for cars in your assigned sub-registers
            {partnerName ? ` — ${partnerName}` : ""}
          </p>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {error ? <div className="admin-error">{error}</div> : null}

      {loading ? (
        <div className="admin-loading">Loading test drives…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-card">
          <p>No test drive requests for your territories yet.</p>
        </div>
      ) : (
        <div className="partner-inbox-list">
          {filtered.map((booking) => (
            <article key={booking.id} className="partner-inbox-card">
              <div className="partner-inbox-head">
                <div>
                  <span className="partner-inbox-label">Vehicle</span>
                  <strong>{booking.carTitle}</strong>
                  {booking.subRegisterName && (
                    <small className="partner-inbox-sub">Territory: {booking.subRegisterName}</small>
                  )}
                </div>
                <span className={`partner-inbox-status status-${booking.status}`}>
                  {STATUS_LABELS[booking.status] || booking.status}
                </span>
              </div>

              <div className="partner-inbox-meta">
                <div>
                  <span>Customer</span>
                  <strong>{booking.name || "—"}</strong>
                </div>
                <div>
                  <span>Phone</span>
                  <strong>
                    {booking.phone ? (
                      <a href={`tel:${booking.phone}`}>{booking.phone}</a>
                    ) : (
                      "—"
                    )}
                  </strong>
                </div>
                <div>
                  <span>Preferred date</span>
                  <strong>{booking.preferredDate}</strong>
                </div>
                <div>
                  <span>Preferred time</span>
                  <strong>{booking.preferredTime}</strong>
                </div>
                {booking.location && (
                  <div>
                    <span>Location</span>
                    <strong>{booking.location}</strong>
                  </div>
                )}
                <div>
                  <span>Assigned partner</span>
                  <strong>{booking.partnerName || partnerName || "You"}</strong>
                </div>
                {booking.notes && (
                  <div className="partner-inbox-notes">
                    <span>Notes</span>
                    <strong>{booking.notes}</strong>
                  </div>
                )}
              </div>

              <div className="partner-inbox-actions">
                <label className="partner-inbox-status-select">
                  <span>Update status</span>
                  <select
                    value={booking.status}
                    disabled={savingId === booking.id}
                    onChange={(e) => updateStatus(booking, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </label>
                {booking.phone && (
                  <a className="admin-btn admin-btn-ghost" href={`tel:${booking.phone}`}>
                    Call customer
                  </a>
                )}
                {booking.phone && (
                  <a
                    className="admin-btn admin-btn-ghost"
                    href={`https://wa.me/91${String(booking.phone).replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
